import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { LdsBadge } from "../../../components/lds/LdsBadge";
import { parseToDate } from "../../../utils/dateUtils";
import { teacherSchoolTasksApi } from "../api";
import { formatTaskStart } from "../taskStart";
import { SchoolTaskPayloadError } from "../parsers";
import type {
  AutoAssignmentApplyProposal,
  AutoAssignmentApplyResult,
  AutoAssignmentPreview,
  AutoAssignmentPreviewFamily,
  AutoAssignmentPreviewProblem,
  Child,
  Family,
  SchoolTask,
} from "../types";

export type AutoAssignmentPreviewFilter = "all" | "proposals" | "unresolved";

export interface AutoAssignmentPreviewDialogProps {
  show: boolean;
  schoolYearId: string;
  schoolYearLabel: string;
  families: Family[];
  familyChildren: Record<string, Child[]>;
  tasks: SchoolTask[];
  locale: string;
  onClose: () => void;
  /** Called after the selected proposals were saved, or when that is unknown, so the page reloads its data. */
  onAssigned?: () => void;
}

type ProposalItem = { id: string; familyId: string; childId: string | null; taskId: string };
type ApplyOutcome =
  | { kind: "applied"; result: AutoAssignmentApplyResult }
  | { kind: "stale" }
  | { kind: "failed"; message?: string }
  | { kind: "unknown" };

const STALE_PREVIEW = "AUTO_ASSIGNMENT_PREVIEW_STALE";
const BEFORE_DEADLINE = "AUTO_ASSIGNMENT_BEFORE_DEADLINE";
const toRequest = (item: ProposalItem): AutoAssignmentApplyProposal => item.childId
  ? { type: "PARENT_TASK", familyId: item.familyId, childId: item.childId, taskId: item.taskId }
  : { type: "GENERAL_CONTRIBUTION", familyId: item.familyId, childId: null, taskId: item.taskId };

const taskItem = (familyId: string, childId: string | null, taskId: string): ProposalItem =>
  ({ id: `task|${familyId}|${childId || ""}|${taskId}`, familyId, childId, taskId });
const proposalItems = (family: AutoAssignmentPreviewFamily): ProposalItem[] => [
  ...family.parentTaskProposals.map((proposal) => taskItem(family.familyId, proposal.childId, proposal.taskId)),
  ...family.generalContributionProposals.map((proposal) => taskItem(family.familyId, null, proposal.taskId)),
];

const key = (id: unknown) => String(id ?? "").toLowerCase();
const shortId = (id: string) => id.slice(0, 8);
const hasProposals = (family: AutoAssignmentPreviewFamily) =>
  family.parentTaskProposals.length > 0 || family.generalContributionProposals.length > 0;

/**
 * The automatic assignment the backend calculated, with the option to save the selected proposals.
 * Every proposal, count and problem is shown as received; the reference data is only used to turn
 * ids into names. The selection is sent in one request together with the preview's fingerprint; the
 * backend saves all of it or nothing, and refuses when the preview no longer matches the data.
 */
export function AutoAssignmentPreviewDialog({ show, schoolYearId, schoolYearLabel, families, familyChildren, tasks, locale, onClose, onAssigned }: AutoAssignmentPreviewDialogProps) {
  const { t } = useTranslation();
  const [loaded, setPreview] = useState<AutoAssignmentPreview>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [filter, setFilter] = useState<AutoAssignmentPreviewFilter>("all");
  const [search, setSearch] = useState("");
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<ApplyOutcome>();
  const [confirming, setConfirming] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const request = useRef(0);
  const assignRun = useRef(0);
  const resetSelection = () => {
    setDeselected(new Set());
    setConfirming(false);
  };
  // Never render a result under another school year, not even for one frame.
  const preview = loaded && key(loaded.schoolYearId) === key(schoolYearId) ? loaded : undefined;

  const load = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    setError(undefined);
    try {
      const result = await teacherSchoolTasksApi.autoAssignmentPreview(schoolYearId);
      if (current !== request.current) return;
      if (key(result.schoolYearId) !== key(schoolYearId)) throw new SchoolTaskPayloadError("Preview belongs to another school year");
      setPreview(result);
      resetSelection();
      // The result of a save stays visible over the preview that was recalculated after it.
      setOutcome((current) => current?.kind === "applied" ? current : undefined);
    } catch (cause: any) {
      if (current !== request.current) return;
      const status = cause?.response?.status;
      // Before the deadline parents still choose themselves: the backend refuses and names the deadline.
      if (status === 409 && cause.response.data?.code === BEFORE_DEADLINE) {
        setPreview(undefined);
        const deadline = typeof cause.response.data.selfSignupDeadline === "string" ? cause.response.data.selfSignupDeadline : null;
        setError(deadline
          ? t("schoolTasks.autoAssignment.beforeDeadline", { defaultValue: "Automatic assignment is not possible yet. Parents can sign up themselves until {{deadline}}; assigning is possible from {{from}}.", deadline: formatLocalDate(deadline, locale), from: formatLocalDate(deadline, locale, 1) })
          : t("schoolTasks.autoAssignment.noDeadline", { defaultValue: "Automatic assignment is not possible yet: this school year has no self-signup deadline. Set it in the school year." }));
        return;
      }
      // Without permission or school year there is nothing left that the old result could describe.
      if (status === 403 || status === 404) setPreview(undefined);
      setError(status === 403
        ? t("schoolTasks.autoAssignment.errors.forbidden", { defaultValue: "You do not have permission to preview the automatic assignment." })
        : status === 404
          ? t("schoolTasks.autoAssignment.errors.yearNotFound", { defaultValue: "This school year could not be found." })
          : cause instanceof SchoolTaskPayloadError
            ? t("schoolTasks.autoAssignment.errors.invalid", { defaultValue: "The preview could not be read." })
            : t("schoolTasks.autoAssignment.errors.load", { defaultValue: "The preview could not be loaded." }));
    } finally {
      if (current === request.current) setLoading(false);
    }
  }, [locale, schoolYearId, t]);

  // A preview belongs to one school year and one opening of the dialog.
  useEffect(() => {
    setPreview(undefined);
    setError(undefined);
    setFilter("all");
    setSearch("");
    resetSelection();
    setOutcome(undefined);
    setAssigning(false);
    if (!show || !schoolYearId) {
      request.current += 1;
      assignRun.current += 1;
      setLoading(false);
      return undefined;
    }
    void load();
    return () => { request.current += 1; assignRun.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schoolYearId]);

  const familyNames = useMemo(() => new Map(families.map((family) => [key(family.id), family.familyName])), [families]);
  const children = useMemo(() => {
    const result = new Map<string, Child>();
    Object.values(familyChildren).forEach((list) => (list || []).forEach((child) => result.set(key(child.id), child)));
    return result;
  }, [familyChildren]);
  // The page reloads its data after an assignment run; keep the names seen while the dialog is open
  // so rows do not turn into "Unknown task" during that reload.
  const seenTasks = useRef(new Map<string, SchoolTask>());
  if (!show) {
    seenTasks.current.clear();
  }
  const taskMap = useMemo(() => {
    tasks.forEach((task) => seenTasks.current.set(key(task.id), task));
    return new Map(seenTasks.current);
  }, [tasks]);

  const familyName = (id: string) => familyNames.get(key(id)) || t("schoolTasks.autoAssignment.unknownFamily", { defaultValue: "Unknown family" });
  const childName = (id: string) => {
    const child = children.get(key(id));
    return [child?.firstName, child?.lastName].filter(Boolean).join(" ") || t("schoolTasks.autoAssignment.unknownChild", { defaultValue: "Unknown child" });
  };
  const taskLabel = (id: string) => {
    const task = taskMap.get(key(id));
    if (!task) return t("schoolTasks.autoAssignment.unknownTask", { defaultValue: "Unknown task" });
    const startsAt = formatTaskStart(task.startsAt, locale, { weekday: "short", day: "numeric", month: "short" });
    return startsAt ? `${task.name} · ${startsAt}` : task.name;
  };
  const typeLabel = (type: string) => type === "HOLIDAY_CLEANING"
    ? t("schoolTasks.types.holiday", { defaultValue: "Holiday cleaning" })
    : t("schoolTasks.types.general", { defaultValue: "General task" });
  const problemText = (problem: AutoAssignmentPreviewProblem) => {
    switch (problem.code) {
      case "NO_CLASS_TASK_CAPACITY":
        return problem.childId
          ? t("schoolTasks.autoAssignment.problems.noClassTaskForChild", { defaultValue: "No available class task could be found for {{child}}.", child: childName(problem.childId) })
          : t("schoolTasks.autoAssignment.problems.noClassTask", { defaultValue: "No available class task could be found." });
      case "NO_GENERAL_CONTRIBUTION_CAPACITY":
        return t("schoolTasks.autoAssignment.problems.noGeneralContributions", { defaultValue: "Not enough available general tasks or holiday cleanings. Still missing: {{count}}.", count: problem.missingCount });
      default:
        return t("schoolTasks.autoAssignment.problems.unknown", { defaultValue: "An obligation could not be filled automatically. Open obligations: {{count}}.", count: problem.missingCount });
    }
  };

  const visibleFamilies = useMemo(() => {
    if (!preview) return [];
    const term = search.trim().toLocaleLowerCase();
    return preview.families
      .filter((family) => filter === "all" || (filter === "proposals" ? hasProposals(family) : family.problems.length > 0))
      .filter((family) => !term || (familyNames.get(key(family.familyId)) || "").toLocaleLowerCase().includes(term))
      .map((family, index) => ({ family, index }))
      .sort((left, right) => {
        const leftName = familyNames.get(key(left.family.familyId));
        const rightName = familyNames.get(key(right.family.familyId));
        if (leftName && rightName) return leftName.localeCompare(rightName, locale) || left.index - right.index;
        if (leftName || rightName) return leftName ? -1 : 1;
        return left.index - right.index;
      })
      .map(({ family }) => family);
  }, [preview, filter, search, familyNames, locale]);

  // The selected proposals, in the order the backend sent them.
  const pending = useMemo(
    () => (preview?.families || []).flatMap(proposalItems).filter((item) => !deselected.has(item.id)),
    [preview, deselected],
  );
  const toggle = (id: string) => setDeselected((current) => {
    const next = new Set(current);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  const selectAll = (selected: boolean) => setDeselected(selected ? new Set() : new Set((preview?.families || []).flatMap(proposalItems).map((item) => item.id)));

  const assignSelected = async () => {
    if (assigning || !preview || !pending.length) return;
    const run = ++assignRun.current;
    setConfirming(false);
    setAssigning(true);
    setOutcome(undefined);
    try {
      const result = await teacherSchoolTasksApi.autoAssignmentApply({
        schoolYearId: preview.schoolYearId,
        inputFingerprint: preview.inputFingerprint,
        proposals: pending.map(toRequest),
      });
      if (run !== assignRun.current) return;
      setOutcome({ kind: "applied", result });
      setAssigning(false);
      onAssigned?.();
      void load();
    } catch (cause: any) {
      if (run !== assignRun.current) return;
      const response = cause?.response;
      const status = response?.status;
      // Without an answer, with an unreadable one, or with a timeout or server error (possibly from a
      // gateway while the backend still committed) the save may or may not have happened.
      // Support view-as refuses writes before sending them: certainly nothing was saved.
      const refusedLocally = cause?.code === "LUMEN_VIEW_AS_READ_ONLY";
      const unknown = !refusedLocally && (!response || typeof status !== "number" || cause instanceof SchoolTaskPayloadError || status === 408 || status >= 500);
      setOutcome(refusedLocally
        ? { kind: "failed", message: cause.message }
        : unknown
        ? { kind: "unknown" }
        : status === 409 && response.data?.code === STALE_PREVIEW
          ? { kind: "stale" }
          : { kind: "failed", message: response.data?.message });
      setAssigning(false);
      if (unknown) onAssigned?.();
    }
  };
  // Escape and the backdrop first leave the confirmation; during a run the dialog stays open.
  const close = () => {
    if (assigning) return;
    if (confirming) setConfirming(false);
    else onClose();
  };
  const rowState = (item: ProposalItem) => ({
    selected: !deselected.has(item.id),
    locked: assigning,
    onToggle: () => toggle(item.id),
    labels: {
      proposed: t("schoolTasks.autoAssignment.status.proposed", { defaultValue: "Proposed" }),
      selected: t("schoolTasks.autoAssignment.status.selected", { defaultValue: "Selected" }),
    },
  });
  const busy = loading || assigning;
  // A stale preview can only be assigned again after it has been recalculated.
  const stale = outcome?.kind === "stale";
  // The backend decides whether proposals may be saved; the preview itself stays available.
  const applyDisabled = Boolean(preview && !preview.applyEnabled);

  const generatedAt = preview ? parseToDate(preview.generatedAt)?.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }) : undefined;
  const notRefreshed = Boolean(preview && error);
  const secondaryButton = "rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800";
  const primaryButton = "rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 aria-disabled:cursor-not-allowed aria-disabled:opacity-60";
  const filters: Array<{ id: AutoAssignmentPreviewFilter; label: string }> = [
    { id: "all", label: t("common.all", { defaultValue: "All" }) },
    { id: "proposals", label: t("schoolTasks.autoAssignment.filters.proposals", { defaultValue: "With proposals" }) },
    { id: "unresolved", label: t("schoolTasks.autoAssignment.filters.unresolved", { defaultValue: "With unresolved obligations" }) },
  ];

  return (
    <Modal
      show={show}
      type="info"
      variant="lds"
      showIcon={false}
      dialogClassName="sm:max-w-4xl"
      title={t("schoolTasks.autoAssignment.title", { defaultValue: "Automatic assignment preview" })}
      onClose={close}
      footer={<div className="sticky -bottom-5 -mx-5 -mb-5 mt-5 border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-900 sm:-bottom-6 sm:-mx-6 sm:-mb-6 sm:px-6">
        {confirming && <div role="group" aria-label={t("schoolTasks.autoAssignment.confirmTitle", { defaultValue: "Confirm assignment" })} className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-400/30 dark:bg-indigo-400/10">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t("schoolTasks.autoAssignment.confirmText", { defaultValue: "Assign the selected proposals now ({{count}})? They are saved together: either all of them or none.", count: pending.length })}</p>
          <div className="mt-3 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => setConfirming(false)} className={secondaryButton}>{t("schoolTasks.autoAssignment.cancel", { defaultValue: "Cancel" })}</button>
            <button type="button" autoFocus onClick={() => void assignSelected()} className={primaryButton}>{t("schoolTasks.autoAssignment.confirm", { defaultValue: "Confirm assignment" })}</button>
          </div>
        </div>}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={close} aria-disabled={assigning} className={secondaryButton}>{t("common.close", { defaultValue: "Close" })}</button>
          <button type="button" onClick={() => { if (!busy) void load(); }} aria-disabled={busy} className={secondaryButton}>{t("schoolTasks.autoAssignment.refresh", { defaultValue: "Refresh preview" })}</button>
          {preview && <button type="button" onClick={() => { if (!busy && !confirming && !stale && !applyDisabled && pending.length) setConfirming(true); }} aria-disabled={busy || confirming || stale || applyDisabled || !pending.length} className={primaryButton}>{assigning
            ? t("schoolTasks.autoAssignment.assigning", { defaultValue: "Assigning…" })
            : t("schoolTasks.autoAssignment.assignSelected", { defaultValue: "Assign selected ({{count}})", count: pending.length })}</button>}
        </div>
      </div>}
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{t("schoolTasks.autoAssignment.intro", { defaultValue: "Review how Lumen would fill the remaining school tasks. Nothing is changed until you assign the selected proposals." })}</p>
          <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
            {t("schoolTasks.autoAssignment.schoolYear", { defaultValue: "School year: {{year}}", year: schoolYearLabel })}
            {generatedAt && <> · {t("schoolTasks.autoAssignment.generatedAt", { defaultValue: "Generated: {{time}}", time: generatedAt })}</>}
            {preview && <> · {t("schoolTasks.autoAssignment.deadlinePassed", { defaultValue: "Self-signup closed after {{deadline}}", deadline: formatLocalDate(preview.selfSignupDeadline, locale) })}</>}
          </p>
        </div>

        {applyDisabled && <div role="note" className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">{t("schoolTasks.autoAssignment.applyDisabled", { defaultValue: "Assigning is switched off. Switch it on in Settings." })}</div>}
        {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">{error}</div>}
        {notRefreshed && <div role="status" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">{t("schoolTasks.autoAssignment.notRefreshed", { defaultValue: "This preview was not refreshed and may be outdated. It shows the result generated at {{time}}.", time: generatedAt || "" })}</div>}
        {outcome?.kind === "applied" && <div role="status" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
          {t("schoolTasks.autoAssignment.applied", { defaultValue: "Assigned: {{parentTasks}} class tasks and {{general}} general contributions. The preview below has been recalculated.", parentTasks: outcome.result.assignedParentTasks, general: outcome.result.assignedGeneralContributions })}
        </div>}
        {outcome?.kind === "stale" && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
          <span>{t("schoolTasks.autoAssignment.stale", { defaultValue: "The situation has changed since this preview was generated. Refresh the preview before assigning. Nothing was assigned." })}</span>
          <button type="button" onClick={() => { if (!busy) void load(); }} aria-disabled={busy} className={secondaryButton}>{t("schoolTasks.autoAssignment.refresh", { defaultValue: "Refresh preview" })}</button>
        </div>}
        {outcome?.kind === "failed" && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {t("schoolTasks.autoAssignment.applyFailed", { defaultValue: "Nothing was assigned." })}{outcome.message && <> {outcome.message}</>}
        </div>}
        {outcome?.kind === "unknown" && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {t("schoolTasks.autoAssignment.applyUnknown", { defaultValue: "It is not known whether the selection was saved. Refresh the preview to see the current situation." })}
        </div>}
        {loading && <div role="status" className="text-center text-sm font-semibold text-slate-500 dark:text-slate-400">
          {!preview && <Spinner />}
          {preview
            ? t("schoolTasks.autoAssignment.refreshing", { defaultValue: "Refreshing preview…" })
            : t("schoolTasks.autoAssignment.loading", { defaultValue: "Calculating preview…" })}
        </div>}

        {preview && <div aria-busy={busy} className={`space-y-4 ${loading ? "opacity-60" : ""}`}>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryTile label={t("schoolTasks.autoAssignment.summary.families", { defaultValue: "Families checked" })} value={preview.summary.familiesConsidered} />
            <SummaryTile label={t("schoolTasks.autoAssignment.summary.classTasks", { defaultValue: "Class tasks proposed" })} value={preview.summary.proposedParentTasks} />
            <SummaryTile label={t("schoolTasks.autoAssignment.summary.general", { defaultValue: "General contributions proposed" })} value={preview.summary.proposedGeneralContributions} />
            <SummaryTile label={t("schoolTasks.autoAssignment.summary.unresolved", { defaultValue: "Unresolved obligations" })} value={preview.summary.unresolvedObligations} attention={preview.summary.unresolvedObligations > 0} />
          </dl>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {filters.map((item) => <button key={item.id} type="button" aria-pressed={filter === item.id} onClick={() => setFilter(item.id)} className={`rounded-full px-4 py-2 text-sm font-bold transition ${filter === item.id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"}`}>{item.label}</button>)}
            </div>
            <div className="flex flex-wrap gap-2 text-sm font-bold">
              <button type="button" onClick={() => { if (!assigning) selectAll(true); }} aria-disabled={assigning} className="text-indigo-600 underline-offset-2 hover:underline aria-disabled:opacity-60 dark:text-indigo-300">{t("schoolTasks.autoAssignment.selectAll", { defaultValue: "Select all" })}</button>
              <button type="button" onClick={() => { if (!assigning) selectAll(false); }} aria-disabled={assigning} className="text-indigo-600 underline-offset-2 hover:underline aria-disabled:opacity-60 dark:text-indigo-300">{t("schoolTasks.autoAssignment.selectNone", { defaultValue: "Deselect all" })}</button>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
              <span>{t("schoolTasks.autoAssignment.searchFamily", { defaultValue: "Search family" })}</span>
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} className="w-44 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
            </label>
          </div>

          <div role="region" tabIndex={0} aria-label={t("schoolTasks.autoAssignment.familyList", { defaultValue: "Proposals per family" })} className="rounded-2xl border border-slate-200 dark:border-slate-700 sm:max-h-[48vh] sm:overflow-y-auto">
            {visibleFamilies.length
              ? <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {visibleFamilies.map((family, position) => <li key={`${family.familyId}-${position}`} data-family-id={family.familyId} className="space-y-3 px-4 py-4">
                  <h3 className="break-words font-black text-slate-950 dark:text-white" title={familyNames.get(key(family.familyId)) ? undefined : shortId(family.familyId)}>{familyName(family.familyId)}</h3>
                  {family.parentTaskProposals.length > 0 && <ProposalGroup title={t("schoolTasks.autoAssignment.classTasks", { defaultValue: "Class tasks" })}>
                    {family.parentTaskProposals.map((proposal) => <ProposalRow key={`${proposal.childId}-${proposal.taskId}`} {...rowState(taskItem(family.familyId, proposal.childId, proposal.taskId))} hint={children.has(key(proposal.childId)) && taskMap.has(key(proposal.taskId)) ? undefined : `${shortId(proposal.childId)} → ${shortId(proposal.taskId)}`}>{childName(proposal.childId)} → {taskLabel(proposal.taskId)}</ProposalRow>)}
                  </ProposalGroup>}
                  {family.generalContributionProposals.length > 0 && <ProposalGroup title={t("schoolTasks.autoAssignment.generalContribution", { defaultValue: "General contribution" })}>
                    {family.generalContributionProposals.map((proposal) => <ProposalRow key={proposal.taskId} {...rowState(taskItem(family.familyId, null, proposal.taskId))} hint={taskMap.has(key(proposal.taskId)) ? undefined : shortId(proposal.taskId)}>{typeLabel(proposal.type)} — {taskLabel(proposal.taskId)}</ProposalRow>)}
                  </ProposalGroup>}
                  {family.problems.length > 0 && <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/60 dark:bg-amber-950/30">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">{t("schoolTasks.autoAssignment.unresolved", { defaultValue: "Could not be filled" })}</h4>
                    <ul className="mt-1 space-y-1">
                      {family.problems.map((problem, index) => <li key={`${problem.code}-${problem.childId || "family"}-${index}`} data-problem-code={problem.code} className="break-words text-sm font-semibold text-amber-900 dark:text-amber-100">{problemText(problem)}</li>)}
                    </ul>
                  </div>}
                  {!hasProposals(family) && family.problems.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">{t("schoolTasks.autoAssignment.noProposals", { defaultValue: "No new assignments proposed." })}</p>}
                </li>)}
              </ul>
              : <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">{preview.families.length
                ? t("schoolTasks.autoAssignment.noFamiliesMatch", { defaultValue: "No families match this filter." })
                : t("schoolTasks.autoAssignment.noFamilies", { defaultValue: "No families take part in the automatic assignment for this school year." })}</div>}
          </div>
        </div>}
      </div>
    </Modal>
  );
}

function SummaryTile({ label, value, attention = false }: { label: string; value: number; attention?: boolean }) {
  return <div data-attention={attention ? "true" : undefined} className={`rounded-2xl border px-4 py-3 ${attention ? "border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30" : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60"}`}>
    <dt className={`break-words text-xs font-bold ${attention ? "text-amber-800 dark:text-amber-200" : "text-slate-500 dark:text-slate-400"}`}>{label}</dt>
    <dd className={`mt-1 text-2xl font-black ${attention ? "text-amber-900 dark:text-amber-100" : "text-slate-950 dark:text-white"}`}>{value}</dd>
  </div>;
}

function ProposalGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return <div>
    <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400">{title}</h4>
    <ul className="mt-1 space-y-1">{children}</ul>
  </div>;
}

/** `hint` carries shortened ids for troubleshooting when a name could not be looked up. */
interface ProposalRowProps {
  hint?: string;
  selected: boolean;
  locked: boolean;
  onToggle: () => void;
  labels: { proposed: string; selected: string };
  children: React.ReactNode;
}

const ProposalRow: React.FC<ProposalRowProps> = ({ hint, selected, locked, onToggle, labels, children }) =>
  <li title={hint} data-state={selected ? "selected" : "proposed"} className="break-words rounded-xl bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
    <label className="flex items-start gap-3">
      <input type="checkbox" className="mt-1" checked={selected} disabled={locked} onChange={onToggle} />
      <span className="min-w-0 flex-1">{children}</span>
      <LdsBadge tone={selected ? "info" : "neutral"} className="shrink-0">{selected ? labels.selected : labels.proposed}</LdsBadge>
    </label>
  </li>;

/** A backend LocalDate ("2026-10-01") as a local calendar date, optionally some days later; never shifted by the time zone. */
export function formatLocalDate(value: string, locale: string, plusDays = 0) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day + plusDays).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}
